import { useEffect, useState } from "react";
import { PhoneCall, CalendarCheck, Inbox, MailWarning, ArrowRight, Phone, ShieldCheck, Clock, Sparkles, CheckCircle2, Circle, Lock, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

const intentColor = {
  booking: "bg-emerald-100 text-emerald-700",
  enquiry: "bg-blue-100 text-blue-700",
  order: "bg-amber-100 text-amber-700",
  complaint: "bg-red-100 text-red-700",
  message: "bg-purple-100 text-purple-700",
  other: "bg-slate-100 text-slate-600",
};

const LIVE_ICON = { calls: PhoneCall, appointments: CalendarCheck, messages: Inbox, unread: MailWarning };

export default function Overview({ onNavigate }) {
  const [dash, setDash] = useState(null);
  const [calls, setCalls] = useState([]);
  const [phone, setPhone] = useState(null);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    const loadDash = () => api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {});
    loadDash();
    api.get("/calls").then((r) => setCalls(r.data.slice(0, 4))).catch(() => {});
    api.get("/verification").then((r) => setPhone(r.data)).catch(() => {});
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
    // Real-time: refresh the dashboard metrics when a payment webhook updates a booking.
    window.addEventListener("payment-updated", loadDash);
    return () => window.removeEventListener("payment-updated", loadDash);
  }, []);

  const V_LABEL = { not_started: "Not started", pending: "Pending", under_review: "Under review", verified: "Verified", action_needed: "Action needed" };
  const widgets = dash?.widgets || [];
  const setup = dash?.setup || [];
  const industry = dash?.industry;
  const isConfigured = dash?.is_configured;
  const remainingSetup = setup.filter((s) => !s.done);

  return (
    <div data-testid="overview-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Overview</h1>
          <p className="text-slate-500 mt-1">
            {industry ? <>Your <span className="font-semibold text-slate-700">{industry}</span> command centre.</> : "Everything your AI receptionist handles, in one place."}
          </p>
        </div>
        {industry && <span data-testid="overview-industry-badge" className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1.5"><Sparkles className="h-3.5 w-3.5" /> {industry}</span>}
      </div>

      {/* Setup guidance for brand-new / partially set-up accounts */}
      {remainingSetup.length > 0 && (
        <div data-testid="setup-checklist" className="mt-6 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white p-6">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center text-white"><Sparkles className="h-4.5 w-4.5" /></div>
            <div>
              <h2 className="font-heading font-bold tracking-tight">{isConfigured ? "Finish setting up" : "Let's get you set up"}</h2>
              <p className="text-sm text-slate-500">A few quick steps to get your AI receptionist ready for real customers.</p>
            </div>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            {setup.map((s) => (
              <button key={s.key} data-testid={`setup-${s.key}`} onClick={() => onNavigate(s.to)}
                className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${s.done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white hover:border-blue-300"}`}>
                {s.done ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
                <span className={`text-sm ${s.done ? "text-slate-500 line-through" : "font-medium text-slate-800"}`}>{s.label}</span>
                {!s.done && <ArrowRight className="h-4 w-4 text-slate-300 ml-auto shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Industry-aware metric widgets */}
      <div className="reveal mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {widgets.map((w) => {
          const Icon = LIVE_ICON[w.key] || (w.key === "revenue" ? TrendingUp : Sparkles);
          const isSoon = w.kind === "soon";
          const clickable = !isSoon && w.to;
          return (
            <button
              key={w.key}
              data-testid={`widget-${w.key}`}
              onClick={() => clickable && onNavigate(w.to)}
              disabled={!clickable}
              className={`text-left p-5 transition-all duration-200 ${clickable ? "card-fancy cursor-pointer" : "rounded-xl border border-dashed border-slate-200 bg-slate-50/60 cursor-default"}`}
            >
              <div className="flex items-center justify-between">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${isSoon ? "bg-slate-200 text-slate-400" : "bg-blue-50 text-blue-600"}`}><Icon className="h-4.5 w-4.5" /></div>
                {isSoon && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-400"><Lock className="h-3 w-3" /> Soon</span>}
              </div>
              {isSoon ? (
                <p className="mt-4 text-sm text-slate-400 leading-snug" data-testid={`widget-${w.key}-hint`}>{w.empty_hint}</p>
              ) : (
                <>
                  <p className="mt-4 font-heading text-3xl font-extrabold tracking-tight">{w.value}</p>
                  <p className="text-sm text-slate-500">{w.label}</p>
                  {w.value === 0 && <p className="text-xs text-slate-400 mt-1">{w.empty_hint}</p>}
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Phone number card */}
      <button data-testid="overview-phone-card" onClick={() => onNavigate("phone-number")}
        className="reveal mt-4 w-full text-left card-fancy p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600"><Phone className="h-5 w-5" /></div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">AI receptionist number</p>
              <p className="font-mono font-bold text-lg text-slate-900" data-testid="overview-phone-number">{phone?.purchased_number || "Not set up yet"}</p>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-slate-400" />
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><p className="text-xs text-slate-400 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Verification</p><p className="font-semibold text-slate-800">{V_LABEL[phone?.status] || "—"}</p></div>
          <div><p className="text-xs text-slate-400">AI status</p><p className={`font-semibold ${phone?.is_live ? "text-emerald-600" : "text-slate-500"}`}>{phone?.is_live ? "Live" : "Not live"}</p></div>
          <div><p className="text-xs text-slate-400">Calls</p><p className="font-semibold text-slate-800">{dash?.counts?.calls ?? 0}</p></div>
          <div><p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> Minutes left</p><p className="font-semibold text-slate-800">{sub?.minutes_remaining ?? "∞"}</p></div>
        </div>
      </button>

      <div className="reveal mt-8 rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-heading font-bold tracking-tight">Recent calls</h2>
          <button data-testid="view-all-calls" onClick={() => onNavigate("calls")} className="text-sm font-medium text-blue-600 flex items-center gap-1 hover:gap-2 transition-all">
            View all <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {calls.length === 0 && (
            <div className="px-5 py-10 text-center" data-testid="calls-empty">
              <div className="mx-auto h-11 w-11 rounded-full bg-blue-50 flex items-center justify-center text-blue-500"><PhoneCall className="h-5 w-5" /></div>
              <p className="mt-3 text-sm font-medium text-slate-700">No calls yet</p>
              <p className="text-sm text-slate-400 mt-0.5">Your AI receptionist is ready to take its first call.</p>
            </div>
          )}
          {calls.map((c) => (
            <div key={c.id} className="px-5 py-4 flex items-start gap-4">
              <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 shrink-0"><PhoneCall className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm text-slate-900">{c.caller_name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${intentColor[c.intent] || intentColor.other}`}>{c.intent}</span>
                </div>
                <p className="text-sm text-slate-600 mt-0.5 truncate">{c.summary}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
